/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Menu, MenuItem, Avatar, Typography, ListItemIcon, Chip, Divider, Box, Grow } from '@material-ui/core';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ExpandMore from '@material-ui/icons/ExpandMore';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import GroupIcon from '@material-ui/icons/Group';
import PeopleIcon from '@material-ui/icons/People';
import { RoutePath } from '../../shared/enums/RoutePath';
import { SwitchAccountUserRoles } from '../../pages/userAccounts/components/SwitchAccountUserRoles';
import Button from '../../components/common/Button';
import useUserMenu from './useUserMenu';
import UserMenuProfile from './UserMenuProfile';
import { AccountType } from '../../shared/enums/AccountType';
import { KeyboardActions } from '../../shared/enums/KeyboardActions';
import { dataTestId } from '../../shared/utils/versionUtils';
import { selectVersion } from '../../redux/features/configuration/configuration.slice';

const useStyles = makeStyles(theme => ({
  menu: {
    minWidth: '360px',
    zIndex: '3000',
    marginTop: '1.5rem',
  },
  root: {
    '&.MuiMenuItem-root': {
      paddingTop: theme.spacing(2),
    },
  },
  padding: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1),
    },
  },
  secondaryProfile: {
    padding: theme.spacing(2),
  },
  roleChips: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: theme.spacing(0.5),
    margin: 0,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      display: 'grid',
      gap: '10%',
      gridTemplateColumns: '1fr 1fr',
    },
  },
  button: {
    padding: 0,
    position: 'relative',
  },
  expandIcon: {
    marginLeft: '3rem',
  },
  menuIcon: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: '1rem',
    },
  },
  listItemIcon: {
    color: theme.palette.black,
    minWidth: 'unset',
    marginRight: '0.5rem',
  },
  rolesMenu: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingTop: '1rem',
    paddingBottom: '1rem',
  },
  large: {
    width: '4.25rem',
    height: '4.25rem',
    marginRight: theme.spacing(2),
  },
  userInitialsText: {
    fontSize: '1.25rem',
    fontWeight: '700',
    marginTop: theme.spacing(0.25),
  },
  avatarPrimary: {
    color: theme.palette.avatar.primary.text,
    backgroundColor: theme.palette.avatar.primary.background,
    position: 'absolute',
    right: '1.3rem',
  },
  avatarPrimarySub: {
    color: theme.palette.avatar.primary.text,
    backgroundColor: theme.palette.avatar.primary.background,
    position: 'relative',
  },
  avatarSecondary: {
    color: theme.palette.avatar.secondary.text,
    backgroundColor: theme.palette.avatar.secondary.background,
    position: 'absolute',
    right: '1.3rem',
  },
  avatarSecondarySub: {
    color: theme.palette.avatar.secondary.text,
    backgroundColor: theme.palette.avatar.secondary.background,
    position: 'relative',
  },
  accountTypeChip: ({ backgroundColor }) => ({
    padding: '1rem',
    paddingRight: '2rem',
    height: theme.spacing(5),
    backgroundColor: backgroundColor || theme.palette.userAccountHeader,
    color: theme.palette.black,
    postion: 'absolute',
    top: '0',
    left: '1rem',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  }),
}));

function UserMenu({ className, user, currentOrganization }) {
  const [showProfileLinkIcon, setShowProfileLinkIcon] = useState(false);
  const { t } = useTranslation();
  const { userData, handleMenu, anchorEl, isOpen, handleClose, handleSignOut } = useUserMenu();
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles({
    backgroundColor: currentOrganization?.attributes?.accentColor,
  });
  const mainUserName = `${user?.firstName} ${user?.lastName}`;

  const isDraft = useMemo(() => ['/draft'].some(word => location.pathname.includes(word)), [location.pathname]);

  function initials(name) {
    return {
      children: name?.initials ?? `${name?.firstName.charAt(0)}${name?.lastName.charAt(0)}`.toUpperCase(),
    };
  }

  useEffect(() => {
    if (currentOrganization?.type === AccountType.ORGANIZATION) {
      setShowProfileLinkIcon(true);
    }
  }, [currentOrganization]);

  const handleKeyDown = (e) => {
    if (e.keyCode === KeyboardActions.ARROW_UP || e.keyCode === KeyboardActions.ARROW_DOWN) {
      handleMenu(e);
    }
  };

  const selectedVersion = useSelector(selectVersion);

  return (
    userData && (
      <nav className={clsx(className)} aria-label={t('aria.header.current.user')}>
        <Button
          aria-label="open"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={handleMenu}
          variant="text"
          color="inherit"
          size="small"
          className={classes.button}
          onKeyDown={handleKeyDown}
        >
          {currentOrganization?.type === AccountType.ORGANIZATION && (
            <Grow in>
              <Chip
                icon={<PeopleIcon />}
                label={currentOrganization?.name}
                color="primary"
                className={classes.accountTypeChip}
              />
            </Grow>
          )}
          <Avatar className={isDraft ? classes.avatarSecondary : classes.avatarPrimary} {...initials(user)} />
          <ExpandMore className={clsx(!showProfileLinkIcon && classes.expandIcon)} />
        </Button>
        <Menu
          aria-label="user-menu"
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          style={{ zIndex: 3000 }}
          classes={{ paper: classes.menu }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          getContentAnchorEl={null}
          open={isOpen}
          onClose={handleClose}
          MenuListProps={{
            classes: { padding: classes.padding },
          }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              history.push(
                RoutePath.generateRoutePath(RoutePath.USER_PROFILE, {
                  userId: userData.sub,
                }),
              );
            }}
          >
            <Avatar
              className={clsx(isDraft ? classes.avatarSecondarySub : classes.avatarPrimarySub, classes.large)}
              {...initials(user)}
            />
            <UserMenuProfile
              onClick={handleClose}
              user={mainUserName}
              userLink={t('user-management.view.profile')}
              showIcon={true}
              to={RoutePath.generateRoutePath(RoutePath.USER_PROFILE, {
                userId: userData.sub,
              })}
            />
          </MenuItem>
          {currentOrganization && (
            <MenuItem
              onClick={() => {
                handleClose();
                history.push(
                  RoutePath.generateRoutePath(RoutePath.USER_SEARCH, {
                    organization: currentOrganization.id,
                  }),
                );
              }}
            >
              <Box className={classes.secondaryProfile}>
                <UserMenuProfile
                  onClick={handleClose}
                  user={t(currentOrganization.type)}
                  userLink={currentOrganization.type === AccountType.ORGANIZATION ? currentOrganization.name : null}
                  showIcon={showProfileLinkIcon}
                  to={RoutePath.generateRoutePath(RoutePath.USER_SEARCH, {
                    organization: currentOrganization.id,
                  })}
                />
              </Box>
            </MenuItem>
          )}
          <Divider className={classes.divider} />
          <MenuItem className={classes.rolesMenu}>
            <Typography variant="inherit" component="h4">
              {t('user-management.your.roles')}
            </Typography>
            <SwitchAccountUserRoles className={classes.roleChips} roles={userData?.roles} />
          </MenuItem>
          <Divider className={classes.divider} />
          <MenuItem
            className={classes.root}
            onClick={() => {
              handleClose();
              history.push(RoutePath.SWITCH_ACCOUNT.path);
            }}
            {...dataTestId({ value: `userMenu-switchAccount--${selectedVersion.value}` })}
          >
            <ListItemIcon classes={{ root: classes.listItemIcon }}>
              <AccountCircleIcon />
            </ListItemIcon>
            <Typography>{t('user-management.switch-account')}</Typography>
          </MenuItem>
          <MenuItem
            className={classes.root}
            onClick={() => {
              handleClose();
              history.push(RoutePath.ORGANIZATION_CREATE.path);
            }}
            {...dataTestId({ value: `userMenu-createOrganization--${selectedVersion.value}` })}
          >
            <ListItemIcon classes={{ root: classes.listItemIcon }}>
              <GroupIcon />
            </ListItemIcon>
            <Typography>{t('user.management.create-organization')}</Typography>
          </MenuItem>
          <MenuItem
            className={classes.root}
            onClick={handleSignOut}
            {...dataTestId({ value: `userMenu-signOut--${selectedVersion.value}` })}
          >
            <ListItemIcon classes={{ root: classes.listItemIcon }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <Typography>{t('Sign-out')}</Typography>
          </MenuItem>
        </Menu>
      </nav>
    )
  );
}

export default UserMenu;
